// useOpenOwings — how many patients are waiting on this product right now.
// Backs the "N patient(s) owing" notice in the adjust/add-stock flows so the
// operator sees, at the moment they touch stock, that adding units will mark
// those backorders fulfillable (they resolve server-side on the stock increase).
import { useBackorders } from '@/features/stocks/api';
import type { BackorderStatus } from '@/domain/types';

const OPEN_STATUSES: BackorderStatus[] = ['Open', 'Awaiting Supplier', 'Partially Fulfilled'];

/** dm+d SKUs may carry a version/source prefix ("5.3.0:<code>") — compare bare codes. */
const bareSku = (s?: string) => {
  const v = s ?? '';
  return v.includes(':') ? v.split(':').pop()! : v;
};

export interface OpenOwings {
  /** Open outbound owings for this product (optionally scoped to a store). */
  count: number;
  /** Total units still pending across those owings. */
  pendingUnits: number;
}

export function useOpenOwings(
  product: { id?: string; sku?: string } | undefined,
  storeId?: string,
): OpenOwings {
  const { data: backorders = [] } = useBackorders();
  if (!product || (!product.id && !product.sku)) return { count: 0, pendingUnits: 0 };
  const sku = bareSku(product.sku);
  const rows = backorders.filter(
    (b) =>
      b.type === 'Outbound' &&
      OPEN_STATUSES.includes(b.status) &&
      (b.pendingQty ?? 0) > 0 &&
      (!storeId || b.store === storeId) &&
      (b.product === product.id || (!!sku && bareSku(b.sku) === sku)),
  );
  return {
    count: rows.length,
    pendingUnits: rows.reduce((s, b) => s + (b.pendingQty ?? 0), 0),
  };
}
